import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore'
import { firestore } from '@/lib/firebase'
import type { FeatureFlagDto } from './response'
import type { FeatureFlagUpsertDto } from './request'

const COLLECTION = 'featureFlags'

function toDto(id: string, data: Record<string, unknown>): FeatureFlagDto {
  const updatedAtRaw = data.updatedAt
  const updatedAt =
    updatedAtRaw instanceof Timestamp ? updatedAtRaw.toDate().toISOString() : undefined

  return {
    key: id,
    enabled: Boolean(data.enabled),
    description: typeof data.description === 'string' ? data.description : '',
    metadata:
      data.metadata && typeof data.metadata === 'object'
        ? (data.metadata as Record<string, unknown>)
        : {},
    updatedAt,
  }
}

export const featureFlagsService = {
  getAll: async (): Promise<FeatureFlagDto[]> => {
    const snap = await getDocs(collection(firestore, COLLECTION))
    return snap.docs.map((d) => toDto(d.id, d.data()))
  },

  getByKey: async (key: string): Promise<FeatureFlagDto | null> => {
    const snap = await getDoc(doc(firestore, COLLECTION, key))
    if (!snap.exists()) return null
    return toDto(snap.id, snap.data())
  },

  upsert: async (dto: FeatureFlagUpsertDto): Promise<void> => {
    await setDoc(
      doc(firestore, COLLECTION, dto.key),
      {
        enabled: dto.enabled,
        description: dto.description,
        metadata: dto.metadata ?? {},
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    )
  },

  setEnabled: async (key: string, enabled: boolean): Promise<void> => {
    await setDoc(
      doc(firestore, COLLECTION, key),
      { enabled, updatedAt: serverTimestamp() },
      { merge: true },
    )
  },

  delete: async (key: string): Promise<void> => {
    await deleteDoc(doc(firestore, COLLECTION, key))
  },
}
